// @ts-check
// Rotas REST da retenção (contrato 10 de atendimento, seção 21.7, e as leituras da tela nas Notas da F5): configuração,
// visão geral da tela Casos de retenção, retenção da família, sinais e o caso.
import { api } from './api-client.js';

/**
 * @typedef {import('../../../shared/retention/retention-settings.js').RetentionSettings} RetentionSettings
 * @typedef {{ id: string, householdId: string, signalKey: string, weight: number, occurredAt: string, expiresAt: string, sourceKind: string,
 *   sourceLabel: string | null, sourceTicketId: string | null, isDismissed: boolean, isActive: boolean, householdName?: string | null }} RetentionSignal
 * @typedef {{ householdId: string, score: number, risk: string | null, threshold: number, updatedAt: string | null, signals: RetentionSignal[],
 *   openCase: { ticketId: string, protocol: string | null, assigneeId: string | null, isVisible: boolean } | null, transferNotice: boolean, canDismiss: boolean }} HouseholdRetention
 * @typedef {{ ticketId: string, protocol: string | null, householdId: string, householdName: string | null, score: number, risk: string | null,
 *   assigneeId: string | null, nextContactAt: string | null, outcome: string | null, openedAt: string, openedBy: string }} RetentionCaseRow
 * @typedef {{ householdId: string, householdName: string | null, score: number, risk: string | null, signalKeys: string[], lastSignalAt: string | null }} RiskyHousehold
 * @typedef {{ threshold: number, cases: RetentionCaseRow[], riskyHouseholds: RiskyHousehold[], recentSignals: RetentionSignal[] }} RetentionOverview
 * @typedef {{ ticketId: string, protocol: string | null, householdId: string | null, householdName: string | null, isOpen: boolean, openedAt: string | null,
 *   openedBy: string | null, outcome: string | null, nextContactAt: string | null,
 *   cadence: Array<{ day: number, taskId: string | null, title: string | null, dueAt: string, isDone: boolean }>,
 *   enrollments: Array<{ id: string, name: string | null, reEnrollmentStatus: string | null, previousStatus: string | null }>, transferNotice: boolean }} RetentionCaseDetail
 */

/** @returns {Promise<{ settings: RetentionSettings, canUpdate: boolean }>} */
export async function fetchRetentionSettings() {
  const body = await api.get('/rest/retention/settings');
  return body?.data;
}

/** @param {Partial<RetentionSettings>} patch @returns {Promise<RetentionSettings>} */
export async function saveRetentionSettings(patch) {
  const body = await api.patch('/rest/retention/settings', patch);
  return body?.data?.settings;
}

/** @returns {Promise<RetentionOverview>} */
export async function fetchRetentionOverview() {
  const body = await api.get('/rest/retention/overview');
  return body?.data;
}

/** @param {string} householdId @returns {Promise<HouseholdRetention>} */
export async function fetchHouseholdRetention(householdId) {
  const body = await api.get(`/rest/retention/households/${householdId}`);
  return body?.data;
}

/** "Marcar risco": sinal da equipe com observação. @param {string} householdId @param {string} note @returns {Promise<RetentionSignal>} */
export async function markHouseholdRisk(householdId, note) {
  const body = await api.post(`/rest/retention/households/${householdId}/signals`, { note });
  return body?.data?.signal;
}

/** @param {string} householdId @param {string} signalId @param {string} note @returns {Promise<RetentionSignal>} */
export async function dismissRetentionSignal(householdId, signalId, note) {
  const body = await api.post(`/rest/retention/households/${householdId}/signals/${signalId}/dismiss`, { note });
  return body?.data?.signal;
}

/** @param {string} householdId @param {string} [note] @returns {Promise<Record<string, any>>} */
export async function openRetentionCase(householdId, note) {
  const body = await api.post('/rest/retention/cases', { householdId, note: note || undefined });
  return body?.data?.ticket;
}

/** @param {string} ticketId @returns {Promise<RetentionCaseDetail>} */
export async function fetchRetentionCase(ticketId) {
  const body = await api.get(`/rest/retention/cases/${ticketId}`);
  return body?.data;
}

/**
 * @param {string} ticketId
 * @param {{ outcome: 'MONITORING' | 'STAYED' | 'LEFT', exitReason?: string | null, enrollmentIds?: string[] | null, note?: string, nextContactAt?: string | null }} input
 * @returns {Promise<Record<string, any>>}
 */
export async function recordRetentionOutcome(ticketId, input) {
  const body = await api.post(`/rest/retention/cases/${ticketId}/outcome`, input);
  return body?.data?.ticket;
}
